import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { einheitenApi, type EinheitUpdateData } from '../api/einheiten';
import { haeuserApi } from '../api/haeuser';
import { adressenApi } from '../api/adressen';
import { anfragenApi } from '../api/anfragen';
import InlineEditField from '../components/InlineEditField';
import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import MapLinkButton from '../components/MapLinkButton';
import { restoreOne } from '../api/softDelete';
import { useCan } from '../hooks/useCan';
import { useIsModuleEnabled } from '../hooks/useConfigVisibility';

export default function ObjektDetail() {
  const { objektId } = useParams<{ objektId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showAdressen = useIsModuleEnabled('adressen');
  const showHaeuser = useIsModuleEnabled('haeuser');
  const showAnfragen = useIsModuleEnabled('anfragen');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data: einheit, isLoading, error } = useQuery({
    queryKey: ['einheit', objektId],
    queryFn: () => einheitenApi.getEinheit(Number(objektId)),
    enabled: !!objektId,
  });

  const { data: haus } = useQuery({
    queryKey: ['haus', einheit?.haus_id],
    queryFn: () => haeuserApi.getHaus(einheit!.haus_id),
    enabled: !!einheit?.haus_id,
  });

  const { data: adresse } = useQuery({
    queryKey: ['adresse', haus?.adresse_id],
    queryFn: () => adressenApi.getAdresse(haus!.adresse_id),
    enabled: !!haus?.adresse_id,
  });

  const { data: anfragenList } = useQuery({
    queryKey: ['einheit', objektId, 'anfragen'],
    queryFn: () => anfragenApi.getAnfragenAtEinheit(Number(objektId)),
    enabled: !!objektId,
  });

  const updateMutation = useMutation({
    mutationFn: (updates: EinheitUpdateData) =>
      einheitenApi.updateEinheit(Number(objektId), updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['einheit', objektId] });
    },
  });

  const canRestore = useCan('restore_own');
  const restoreMutation = useMutation({
    mutationFn: () => restoreOne('einheit', Number(objektId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['einheit', objektId] });
      queryClient.invalidateQueries({ queryKey: ['einheiten'] });
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error || !einheit) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600">Fehler</h2>
          <p className="mt-2 text-gray-600">Objekt konnte nicht geladen werden.</p>
          <button onClick={() => navigate('/objekte')} className="mt-4 text-blue-600 hover:text-blue-800">
            Zurück zur Übersicht
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <button onClick={() => navigate('/objekte')} className="text-blue-600 hover:text-blue-800 text-sm mb-2">
            &larr; Zurück zur Übersicht
          </button>
          <div className="flex items-start justify-between">
            <h1 className="text-2xl font-bold text-gray-900">{einheit.bezeichnung}</h1>
            <div className="flex items-center gap-2">
              {einheit.deleted_at ? (
                <>
                  <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-slate-200 text-slate-700">
                    Ausgeblendet
                  </span>
                  {canRestore && (
                    <button
                      onClick={() => restoreMutation.mutate()}
                      disabled={restoreMutation.isPending}
                      className="px-4 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100"
                    >
                      Wiederherstellen
                    </button>
                  )}
                </>
              ) : (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100"
                >
                  Ausblenden
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
            {adresse && showAdressen && (
              <button onClick={() => navigate(`/adressen/${adresse.id}`)} className="text-blue-600 hover:underline">
                {adresse.strasse} {adresse.hausnummer}
              </button>
            )}
            {adresse && showAdressen && haus && showHaeuser && <span className="text-gray-400">&gt;</span>}
            {haus && showHaeuser && (
              <button onClick={() => navigate(`/haeuser/${haus.id}`)} className="text-blue-600 hover:underline">
                {haus.bezeichnung}
              </button>
            )}
          </div>
          {adresse?.latitude != null && adresse?.longitude != null && (
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <MapLinkButton to={`/karte?objekt=${einheit.id}`} />
            </div>
          )}
        </div>
      </header>

      <ConfirmDeleteModal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        entity="einheit"
        ids={[Number(objektId)]}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['einheiten'] });
          navigate('/objekte');
        }}
      />

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Objektdaten */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-medium">Objektdaten</h3>
          </div>
          <div className="px-6">
            <dl className="divide-y divide-gray-200">
              <InlineEditField label="Bezeichnung" value={einheit.bezeichnung} onSave={(v) => updateMutation.mutate({ bezeichnung: v })} />
              <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
                <dt className="text-sm font-medium text-gray-500">Typ</dt>
                <dd className="mt-1 sm:mt-0 sm:col-span-2">
                  <select
                    value={einheit.typ || ''}
                    onChange={(e) => updateMutation.mutate({ typ: e.target.value || null })}
                    className="bg-transparent border border-transparent rounded px-2 py-1 text-sm text-gray-900 hover:border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none transition-colors"
                  >
                    <option value="">-- kein Typ --</option>
                    <option value="wohnung">Wohnung</option>
                    <option value="privatzimmer">Privatzimmer</option>
                    <option value="gewerbe">Gewerbe</option>
                  </select>
                </dd>
              </div>
              <InlineEditField label="Gäste" value={einheit.guests_count?.toString() || ''} placeholder="Nicht angegeben" onSave={(v) => updateMutation.mutate({ guests_count: v ? parseInt(v) : null })} />
              <InlineEditField label="Schlafzimmer" value={einheit.bedrooms?.toString() || ''} placeholder="Nicht angegeben" onSave={(v) => updateMutation.mutate({ bedrooms: v ? parseInt(v) : null })} />
              <InlineEditField label="Größe (m²)" value={einheit.size_sqm?.toString() || ''} placeholder="Nicht angegeben" onSave={(v) => updateMutation.mutate({ size_sqm: v ? parseFloat(v) : null })} />
              <InlineEditField label="Reg.-Nr." value={einheit.registrierungsnummer || ''} placeholder="Nicht angegeben" onSave={(v) => updateMutation.mutate({ registrierungsnummer: v })} />
              <InlineEditField label="Notizen" value={einheit.notes || ''} placeholder="Keine Notizen" onSave={(v) => updateMutation.mutate({ notes: v })} multiline />
            </dl>
          </div>
        </div>

        {/* Anfragen at this Einheit */}
        {showAnfragen && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-medium">Anfragen ({anfragenList?.length || 0})</h3>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adresse</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notizen</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Erstellt</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {anfragenList?.map((a) => (
                <tr
                  key={a.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/anfragen/${a.id}`)}
                >
                  <td className="px-4 py-3 text-sm font-medium text-blue-600">
                    {a.adresse_text || `#${a.id}`}
                    {a.zipcode && <span className="text-gray-500 font-normal ml-1">{a.zipcode}</span>}
                    {a.city && <span className="text-gray-500 font-normal ml-1">{a.city}</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">{a.notes || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                    {new Date(a.created_at).toLocaleDateString('de-DE')}
                  </td>
                </tr>
              ))}
              {(!anfragenList || anfragenList.length === 0) && (
                <tr>
                  <td colSpan={3} className="px-4 py-8 text-center text-gray-500">
                    Keine Anfragen an diesem Objekt.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}

        {/* Standort breadcrumb */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-medium">Standort</h3>
          </div>
          <div className="px-6 py-4">
            <div className="flex items-center gap-2 text-sm">
              {adresse && showAdressen && (
                <button onClick={() => navigate(`/adressen/${adresse.id}`)} className="text-blue-600 hover:underline font-medium">
                  {adresse.strasse} {adresse.hausnummer}
                  {adresse.plz && `, ${adresse.plz}`}
                </button>
              )}
              {adresse && showAdressen && haus && showHaeuser && <span className="text-gray-400">&gt;</span>}
              {haus && showHaeuser && (
                <button onClick={() => navigate(`/haeuser/${haus.id}`)} className="text-blue-600 hover:underline font-medium">
                  {haus.bezeichnung}
                </button>
              )}
              {haus && showHaeuser && <span className="text-gray-400">&gt;</span>}
              <span className="font-medium text-gray-900">{einheit.bezeichnung}</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
